/**
 * Created by Ning Jiang on 2021-05-05.
 */

import * as PIXI from "pixi.js";
import {BLEND_MODES} from "pixi.js";
import {DDay} from "../../DDay";
import {DDayGameAssets} from "../../DDayGameAssets";
import {DDayLayers, Layers} from "../DDayLayers";
import {Linear, TimelineLite} from "gsap";
import {MechanicalCounter, MechanicalCounterTextureSet} from "../../utils/MechanicalCounter";
import {TimelineSprite} from "@nolimitcity/slot-launcher/bin/animation/TimelineSprite";
import {NolimitApplication} from "@nolimitcity/slot-launcher/bin/NolimitApplication";
import {DDayGameConfig} from "../../DDayGameConfig";
import {BlitzKriegReelController} from "../../reels/BlitzKriegReelController";
import {DDaySymbol} from "../../symbol/DDaySymbol";
import {GameHelper} from "../../../core/utils/GameHelper";
import {IFeatureData} from "../../server/data/IBlitzKriegData";
import {BlitzKriegSound} from "../../sound/BlitzKriegSound";
import {BlitzKriegSymbolState} from "../../symbol/DDaySymbolConfig";

export class DDayReelBackground extends PIXI.Container {
    private _reelFrame: PIXI.Sprite;
    private _waysFrame: PIXI.Sprite;
    private _staticCharge: TimelineSprite;
    private _waysCount: MechanicalCounter;
    private _radio: PIXI.spine.Spine;
    private _radioKnob: PIXI.Sprite;
    private _radioSignals: PIXI.spine.Spine[] = [];
    private _radioSignalsContainer: PIXI.Container;
    private img: TimelineSprite;
    private _radioTl: TimelineLite;
    private _wires: PIXI.Sprite;
    private _logo: PIXI.Sprite;
    private easterEggEnabled: boolean = false;
    private _easterEggTl: TimelineLite;

    constructor() {
        super();
        this.init();
    }

    private init() {
        this._staticCharge = new TimelineSprite(DDay.assetLoader.getTextures("static_charge"));
        this._staticCharge.blendMode = BLEND_MODES.SCREEN;
        this._staticCharge.anchor.set(0.5, 0.5);
        this._staticCharge.position.set(630, 170);
        this._staticCharge.alpha = 0;
        DDayLayers.getLayer(Layers.RADIO_SIGNAL)?.addChild(this._staticCharge);

        this._radioSignalsContainer = new PIXI.Container();

        this._reelFrame = new PIXI.Sprite(DDay.assetLoader.getTextures(DDayGameAssets.REEL_FRAME)[0]);
        this._reelFrame.position.set(360, 360);
        this._reelFrame.anchor.set(0.5, 0.5);

        this._radio = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("radio"));
        this._radio.state.setAnimation(0, "normal", false);
        this._radio.position.set(360, 360);
        this._radio.alpha = 0;

        this._radioKnob = new PIXI.Sprite(DDay.assetLoader.getTextures("knob")[0]);
        this._radioKnob.anchor.set(0.5, 0.5);
        this._radioKnob.position.set(511, 152);
        this._radioKnob.alpha = 0;

        let radio1 = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("radio1"));
        radio1.state.setAnimation(0, "loop_1", false);
        radio1.name = "1";
        radio1.position.set(360, 360);
        radio1.alpha = 0;

        let radio2 = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("radio2"));
        radio2.state.setAnimation(0, "loop_2", false);
        radio2.name = "2";
        radio2.position.set(360, 360);
        radio2.alpha = 0;

        let radio3 = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("radio3"));
        radio3.state.setAnimation(0, "loop_3", false);
        radio3.name = "3";
        radio3.position.set(360, 360);
        radio3.alpha = 0;

        let radio4 = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("radio4"));
        radio4.state.setAnimation(0, "loop_4", false);
        radio4.name = "4";
        radio4.position.set(360, 360);
        radio4.alpha = 0;

        this._radioSignals.push(radio1, radio3, radio2, radio4);
        this._radioSignalsContainer.addChild(radio1, radio2, radio3, radio4);
        this._wires = new PIXI.Sprite(DDay.assetLoader.getTextures("wires")[0]);
        this._wires.anchor.set(0.5, 0.5);
        this._wires.position.set(625, 170);
        this._wires.alpha = 0;

        this._logo = new PIXI.Sprite(DDay.assetLoader.getTextures("logo")[0]);
        this._logo.anchor.set(0.5, 0.5);
        this._logo.position.set(-60, 60);
        this._logo.scale.set(0.75, 0.75);
        this._logo.alpha = 0;

        this._waysFrame = new PIXI.Sprite(DDay.assetLoader.getTextures("waysFrame")[0]);
        this._waysFrame.position.set(653, 254);
        this._waysFrame.anchor.set(0.5, 0.5);
        this._waysFrame.alpha = 0;


        const textures: MechanicalCounterTextureSet = {
            numbers: DDay.assetLoader.getTextures(DDayGameAssets.WIN_WAYS_COUNTER_NUMBERS)[0]
        }
        this._waysCount = new MechanicalCounter(6, textures, 20);
        this._waysCount.alpha = 0;
        this._waysCount.setValue(0);
        this._waysCount.position.set(599, 259);

        DDayLayers.getLayer(Layers.REEL_BACKGROUND)?.addChild(this._reelFrame, this._waysFrame, this._waysCount);
        DDayLayers.getLayer(Layers.RADIO_SIGNAL)?.addChild(this._radio, this._radioKnob, this._wires, this._radioSignalsContainer);
        DDayLayers.getLayer(Layers.LOGO)?.addChild(this._logo);
        DDayLayers.getLayer(Layers.LOGO)!.onResize = () => {
            this.onResize();
        }
        this.onOrientationChanged();
        this.onResize();
    }

    public setXWaysCount(value: number): void {
        this._waysCount.setValue(value);
    }

    public disableXWaysCount(disable: boolean = false): void {
        this._waysCount.alpha = disable ? 0 : 1;
    }

    public onOrientationChanged() {
        // TODO: check restore, mode, orientation
    }

    public enableLogo(enable: boolean, duration: number = 0.2): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.to(this._logo, {alpha: enable ? 1 : 0, duration: duration});
        return tl;
    }

    public playIntroAnimation(): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.fromTo(this._reelFrame, {
            y: 360 - 1000,
            alpha: 1
        }, {
            y: 360,
            duration: 1,
            ease: Linear.easeInOut
        }, 0);
        tl.fromTo(this._wires, {
            y: 170 - 1000,
            alpha: 1
        }, {
            y: 170,
            duration: 1,
            ease: Linear.easeInOut
        }, 0);
        tl.fromTo(this._radio, {
            y: 360 - 1000,
            alpha: 1
        }, {
            y: 360,
            duration: 1,
            ease: Linear.easeInOut
        }, 0);
        tl.fromTo([this._radioKnob], {
            y: 152 - 1000,
            alpha: 0
        }, {
            y: 152,
            duration: 1,
            ease: Linear.easeInOut
        }, 0);
        tl.fromTo(this._waysFrame, {
            y: 254 - 1000,
            alpha: 1
        }, {
            y: 254,
            duration: 1,
            ease: Linear.easeInOut
        }, 0);
        tl.fromTo(this._waysCount, {
            y: 259 - 1000,
            alpha: 1
        }, {
            y: 259,
            duration: 1,
            ease: Linear.easeInOut
        }, 0);
        tl.add(this.playStopStaticCharge());
        return tl;
    }

    public playStopStaticCharge(play: boolean = true): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.add(() => {
            if (play) {
                this._staticCharge.alpha = 1;
                this._staticCharge.playLoop();
            } else {
                this._staticCharge.stopLoop();
                this._staticCharge.alpha = 0;
            }
        });
        return tl;
    }

    public playStaticChargeTwoTimes() {
        this._staticCharge.alpha = 1;
        let tl: TimelineLite = new TimelineLite({
            repeat: 2
        });
        let isAlreadyPlayed: boolean = false;
        tl.add([this._staticCharge.getAnimationAutoShowHide(true, true), () => {
            if (!isAlreadyPlayed) {
                isAlreadyPlayed = true;
                BlitzKriegSound.playTrack({name: BlitzKriegSound.WAYS_ELECTRICITY});
            }
        }]);
        return tl;
    }

    public enableTankBackgroundEffect() {
        this.img = new TimelineSprite(DDay.assetLoader.getTextures("reel_smoke/reel_smoke"));
        this.img.blendMode = BLEND_MODES.SCREEN;
        this.img.anchor.set(0.5, 0.5);
        this.img.position.set(360, 360);
        this.img.scale.set(6, 6);
        this.img.alpha = 0;
        DDayLayers.getLayer(Layers.REEL_BACKGROUND)?.addChildAt(this.img, 0);
        this.img.playLoop();
        this.img.alpha = 0.3
        let tl: TimelineLite = new TimelineLite();
        tl.to(this.img, {
            alpha: 1,
            duration: 1
        });
        tl.to(this, {duration: 3});
        tl.to(this.img, {
            alpha: 0,
            duration: 0.3
        });
        tl.add(() => {
            this.img.stopLoop();
            DDayLayers.getLayer(Layers.REEL_BACKGROUND)?.removeChild(this.img);
        })
        return tl;
    }

    public enableRadioAndWaysFrame(): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.to([this._waysFrame, this._waysCount, this._radio, this._wires], {
            alpha: 1
        });
        return tl;
    }

    public addScreenGlow(): TimelineLite {
        let glow1: PIXI.Sprite = new PIXI.Sprite(DDay.assetLoader.getTextures("screenGlow")[0]);
        let glow2: PIXI.Sprite = new PIXI.Sprite(DDay.assetLoader.getTextures("screenGlow")[0]);
        glow1.alpha = 0;
        glow2.alpha = 0;
        DDayLayers.getLayer(Layers.REEL_BACKGROUND)?.addChild(glow1, glow2);
        glow1.anchor.set(0.5, 0.5);
        glow2.anchor.set(0.5, 0.5);
        glow1.scale.set(5, 5);
        glow2.scale.set(5, 5);
        glow1.position.set(0, glow1.height / 2.8);
        glow2.position.set((NolimitApplication.screenBounds.width / 2) + 50, glow2.height / 3);
        glow1.blendMode = BLEND_MODES.SCREEN;
        glow2.blendMode = BLEND_MODES.SCREEN;
        glow1.rotation = DDayGameConfig.degToRad(90);
        glow2.rotation = DDayGameConfig.degToRad(-190);
        let tl: TimelineLite = new TimelineLite({
            repeat: 12, onComplete: () => {
                DDayLayers.getLayer(Layers.REEL_BACKGROUND)?.removeChild(glow1, glow2);
            }
        });
        tl.to([glow1, glow2], {
            alpha: 1,
            duration: 0.05
        });
        tl.to([glow1, glow2], {
            alpha: 0,
            duration: 0.1
        });
        return tl;
    }

    public addBomberPlaneGlow(): TimelineLite {
        let glow1: PIXI.spine.Spine = new PIXI.spine.Spine(DDay.assetLoader.getSpineAsset("introLight"));
        DDayLayers.getLayer(Layers.REEL_BACKGROUND)?.addChild(glow1);
        glow1.state.setAnimation(0, "light_loop", true);
        glow1.position.set(360, 360);
        glow1.tint = 0xFFA500;
        let tl: TimelineLite = new TimelineLite({
            onComplete: () => {
                DDayLayers.getLayer(Layers.REEL_BACKGROUND)?.removeChild(glow1);
            }
        });
        tl.to(this, {duration: 1});
        return tl;
    }

    public playRadioSignal(level: number): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.add(this.resetRadioSignal());
        if (level === 5) {
            tl.add(() => {
                this._radio.alpha = 1;
                this._radio.visible = true;
            });
            tl.add(DDay.createTimelineForAnimation(this._radio, 0, "signal_" + level, false, 1, 0.2));
            tl.add(DDay.createTimelineForAnimation(this._radio, 0, "loop_" + level, false, 1, 0.2).repeat(2));
        } else {
            let radio: PIXI.spine.Spine = this._radioSignals[level - 1];
            tl.add(() => {
                radio.alpha = 1;
                radio.visible = true;
            });
            tl.add(DDay.createTimelineForAnimation(radio, 0, "loop_" + radio.name, false, 1, 0.2));
            tl.add(DDay.createTimelineForAnimation(radio, 0, "signal_" + radio.name, true, 1, 0.2));
        }
        this._radioTl = tl;
        return tl;
    }

    public playRadioSignalForFSSFS(level: number): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        if (level === 5) {
            tl.add(() => {
                this._radio.alpha = 1;
                this._radio.visible = true;
            });
            tl.add(DDay.createTimelineForAnimation(this._radio, 0, "loop_" + level, false, 1, 0.2).repeat(2));

        } else {
            let radio: PIXI.spine.Spine = this._radioSignals[level - 1];
            tl.add(() => {
                radio.alpha = 1;
                radio.visible = true;
            });
            tl.add(DDay.createTimelineForAnimation(radio, 0, "loop_" + radio.name, false, 1, 0.2));
        }
        this._radioTl = tl;
        return tl;
    }

    public playRadioSignalForFSSFSLoop(position: number): TimelineLite {
        if (this._radioTl && this._radioTl.isActive()) {
            this._radioTl.totalProgress(1);
            this._radioTl.kill();
        }
        let tl: TimelineLite = new TimelineLite();
        if (position !== 4) {
            DDay.model.parsedGameData.featureInfo.forEach((feature: IFeatureData) => {
                let featureIdx: number = feature.Index;
                let radio: PIXI.spine.Spine = this._radioSignals[featureIdx];
                if (feature.Index === position) {
                    tl.add(DDay.createTimelineForAnimation(radio, 0, "signal_" + radio.name, true, 1, 0.2));
                } else if (feature.Index !== 4) {
                    DDay.createTimelineForAnimation(radio, 0, "loop_" + radio.name, false, 1, 0.2, false).totalProgress(1);
                }
            });
        }
        this._radioTl = tl;
        return tl;
    }

    public playRadioIdleAnimation(loop: boolean = true): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        tl.add(this.resetRadioSignal());
        tl.add(DDay.createTimelineForAnimation(this._radio, 0, "idle", false, 1).repeat(loop ? 2 : 0));
        this._radioTl = tl;
        return tl;
    }

    public resetRadioSignal(shouldStop: boolean = true): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        if (this._radioTl && this._radioTl.isActive()) {
            this._radioTl.totalProgress(1);
            this._radioTl.kill();
        }
        if (shouldStop) {
            this._radioSignals.forEach((radioSignal: PIXI.spine.Spine, idx: number) => {
                tl.add(() => {
                    radioSignal.alpha = 0;
                    GameHelper.resetSpine(radioSignal, false);
                }, 0);
            });
            tl.add(DDay.createTimelineForAnimation(this._radio, 0, "normal", false, 1), 0);
        }
        return tl;
    }

    public updateXWaysCount(): void {
        let symbolSize: number[][] = DDay.model.parsedGameData.symbolSizes;
        let count: number = 1;
        symbolSize.forEach((size: number[]) => {
            let multipliers: number = 0;
            size.forEach((multiplier: number) => {
                multipliers = multipliers + multiplier;
            });
            count = count * multipliers;
        });
        this.setXWaysCount(count);
    }

    public getXWaysCountByFeature(): TimelineLite {
        let tl: TimelineLite = new TimelineLite();
        let totalXWaysCount: number = 1;
        DDay.reels.forEach((reel: BlitzKriegReelController) => {
            let totalSymbolsCountPerReel: number = 0;
            reel.reelView.allSymbols.forEach((symbol: DDaySymbol, symIdx: number): void => {
                if (symIdx > 0 && symIdx <= reel.getVisibleSymbols()) {
                    totalSymbolsCountPerReel = totalSymbolsCountPerReel + symbol.getSymbolMultiplier() + (symbol.isSplit ? 1 : 0);
                }
            });
            totalXWaysCount = totalXWaysCount * totalSymbolsCountPerReel;
        });
        if (totalXWaysCount !== this._waysCount.currentValue) {
            tl.add(this.playStaticChargeTwoTimes());
            tl.add(() => {
                this.setXWaysCount(totalXWaysCount);
                BlitzKriegSound.playTrack({name: BlitzKriegSound.WAYS_UPDATE.next()});
            }, ">-=0.1");
        }
        return tl;
    }

    public onResize() {
        // TODO: check restore, mode, orientation
        if (NolimitApplication.isLandscape) {
            this._logo.position.set(-60, 60);
            this._logo.scale.set(0.75, 0.75);
        } else {
            this._logo.position.set(360, -150);
            this._logo.scale.set(1, 1);
        }
        this._logo.visible = !GameHelper.isIpad();
    }

    public enableKnobClick() {
        this._radioKnob.interactive = true;
        this._radioKnob.buttonMode = true;
        this._radioKnob.on('mousedown', () => {
            this.easterEggEnabled = !this.easterEggEnabled;
            this.disableKnobClick();
            this.enableDisableEasterEgg();
        });
        this._radioKnob.on('tap', () => {
            this.easterEggEnabled = !this.easterEggEnabled;
            this.disableKnobClick();
            this.enableDisableEasterEgg();
        });
    }

    public enableDisableEasterEgg(forceStop: boolean = false, isFirstTime: boolean = true) {
        if (forceStop) {
            this.easterEggEnabled = false;
            this.disableKnobClick();
        }
        let tl: TimelineLite = new TimelineLite().timeScale(0.6);

        if (this.easterEggEnabled) {
            DDay.reels.forEach((reel: BlitzKriegReelController) => {
                reel.reelView.allSymbols.forEach((symbol) => {
                    if (symbol.symbolName.includes("L") && !symbol.isSplit) {
                        if (this.easterEggEnabled) {
                            tl.add(symbol.getStateAnimation(BlitzKriegSymbolState.GROOVE, {}, {}), 0);
                        }
                    }
                });
            });
            this._staticCharge.alpha = 1;
            if (isFirstTime) {
                tl.add(DDay.createTimelineForAnimation(this._radio, 0, "press_button", false), 0);
                tl.add(() => {
                    BlitzKriegSound.playTrack({name: BlitzKriegSound.EASTER_EGG});
                }, 0)
            }

            tl.add(this._staticCharge.getAnimationAutoShowHide(true, true), 0);
            tl.add(() => {
                BlitzKriegSound.playTrack({name: BlitzKriegSound.WAYS_ELECTRICITY});
            }, 0)
            tl.add(() => {
                this.enableDisableEasterEgg(false, false);
            });
            this._easterEggTl = tl;
        } else if (this._easterEggTl && this._easterEggTl.isActive()) {
            this._easterEggTl.totalProgress(1);
            this._easterEggTl.kill();
            this._staticCharge.alpha = 0;
        }
    }

    private disableKnobClick() {
        this._radioKnob.interactive = false;
        this._radioKnob.buttonMode = false;
        this._radioKnob.off('mousedown');
        this._radioKnob.off('tap');
    }
}
